import React, { useCallback, useContext, useEffect, useState } from "react";
import { BsQuestionCircle } from "react-icons/bs";
import { getQuizQuestions, getQuizResults, setQuizResult } from "../../api";
import { Auth } from "../../contexts/ridersUserContext";
import { FormattedMessage } from "react-intl";
import { createEncryptor } from "simple-encryptor";
import { Spin, Tag } from "antd";
import { LanguageContext } from "../../contexts/languageContext";
import cn from "classnames";

const letters = ["A", "B", "C", "D", "E", "F"];
const encryptor = createEncryptor("simpleKeyForSimpleEncryptor");

export const QAChapter = ({ quizId }: { quizId: string }) => {
  const encryptMessage = useCallback((message: any) => {
    if (typeof message === "string") {
      return encryptor.encrypt(message);
    } else if (typeof message === "object") {
      return encryptor.encrypt(JSON.stringify(message));
    }
    return "ERROR";
  }, []);

  const { getToken }: any = useContext<any>(Auth.context);
  const [quiz, setQuiz] = useState<any>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [result, setResult] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const { language } = useContext<any>(LanguageContext);

  const loadQuiz = async () => {
    setIsLoading(true);
    try {
      const [quizRes, resultRes]: any = await Promise.all([
        getQuizQuestions(getToken(), quizId, language),
        getQuizResults(getToken(), quizId),
      ]);

      const questions = quizRes.data;
      const resultData = resultRes?.data || [];

      setQuiz(questions);

      if (resultData.length > 0) {
        // Cevap varsa inputlara yansıt
        const restoredAnswers: Record<string, string> = {};
        resultData.forEach((r: any) => {
          if (r.answer) {
            restoredAnswers[r.question] = r.answer;
          }
        });
        setAnswers(restoredAnswers);
        setResult(resultData);
      }
    } catch (error) {
      console.error("Quiz yükleme hatası:", error);
      alert("Quiz yüklenirken bir hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadQuiz().then();
  }, []);

  const handleSelect = (questionId: string, optionId: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: optionId }));
  };

  const handleSubmit = async () => {
    const payload = Object.entries(answers).map(([question, answer]) => ({
      question,
      answer,
    }));

    try {
      const res: any = await setQuizResult(
        getToken(),
        quizId,
        encryptMessage(payload)
      );
      setResult(res.data);
    } catch (error) {
      console.error("Gönderim hatası:", error);
    }
  };

  const getAnswerText = (
    question: any,
    optionId: number | null
  ): {
    option: JSX.Element | string;
    key: number;
  } => {
    if (optionId === null)
      return {
        option: (
          <em>
            {language?.toLowerCase() === "en" ? "Unanswered" : "Boş Bırakıldı"}
          </em>
        ),
        key: -1,
      };
    const option = question.options.find((opt: any) => opt.id === optionId);
    return {
      option: option ? (
        <span dangerouslySetInnerHTML={{ __html: option.text }} />
      ) : language?.toLowerCase() === "en" ? (
        "You didn't answer that question"
      ) : (
        "Bu soruyu cevaplamadın"
      ),
      key: option ? question.options.indexOf(option) : -1,
    };
  };

  if (isLoading)
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Spin size="large" />
      </div>
    );

  return (
    <div className="flex flex-col space-y-5">
      {/* Başlık */}
      <div className="flex items-end">
        <img
          src="https://d32lhafmwoak77.cloudfront.net/utils/icons/pen.svg"
          alt="QA Icon"
          className="w-10 h-10 mr-2"
        />
        <div className="flex flex-col space-y-0">
          <div
            className="text-xs text-[#6D85D9]"
            style={{ lineHeight: "1.2rem" }}
          >
            {quiz?.name}
          </div>
          <div
            className="text-2xl font-extralight"
            style={{ lineHeight: "1.5rem" }}
          >
            <FormattedMessage id="qa_title" />
          </div>
        </div>
      </div>

      {/* Sorular */}
      {quiz?.questions?.map((question: any, i: number) => {
        return (
          <div key={question.id} className="flex flex-col space-y-3 pb-4">
            <div className="flex items-start space-x-3">
              <span className="text-[#049BC9] text-2xl pt-1">
                <BsQuestionCircle />
              </span>
              <div className="flex flex-col space-y-2">
                <span className="text-xl font-semibold">{`${
                  language?.toLowerCase() === "en" ? "Question" : "Soru"
                } ${i + 1}`}</span>
                <span
                  className="text-gray-600"
                  dangerouslySetInnerHTML={{ __html: question.text }}
                ></span>

                {/* Seçenekler */}
                <div className="flex flex-col">
                  {question.options.map((answer: any, index: number) => (
                    <div
                      key={answer.id}
                      className="flex items-center space-x-2"
                    >
                      <input
                        type="radio"
                        id={`answer-${answer.id}`}
                        name={`question-${question.id}`}
                        className="w-4 h-4"
                        checked={answers[question.id] === answer.id}
                        onChange={() => handleSelect(question.id, answer.id)}
                        disabled={result.length > 0}
                      />
                      <span>{letters[index]})</span>
                      <label
                        htmlFor={`answer-${answer.id}`}
                        className="text-gray-700"
                      >
                        <span
                          dangerouslySetInnerHTML={{ __html: answer.text }}
                        ></span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {/* Submit Butonu */}
      {quiz?.questions?.length > 0 && (
        <button
          id="chapter-sidebar-next"
          onClick={handleSubmit}
          className={cn({
            "flex text-white items-center justify-center duration-300 bg-[#059BCA] py-2 px-3 cursor-pointer rounded-full w-40 self-center mt-4":
              true,
            "bg-gray-400 cursor-not-allowed": result.length > 0,
          })}
          disabled={result.length > 0}
        >
          <span>
            <FormattedMessage id="submitQuiz" />
          </span>
        </button>
      )}

      <hr />

      {quiz?.questions?.length > 0 &&
        result.length !== 0 &&
        quiz?.questions?.map((question: any, i: number) => {
          const questionResult = result?.find(
            (r) => r.question === question.id
          );
          const userAnswerId = questionResult?.answer ?? 0;
          const correctAnswerId = questionResult?.correct_answer ?? null;
          const isCorrect = userAnswerId === correctAnswerId;

          const letter = letters[getAnswerText(question, userAnswerId).key];
          const correctLetter =
            letters[getAnswerText(question, correctAnswerId).key];

          return (
            result.length > 0 && (
              <div className="px-8 py-4 text-gray-700 border-b">
                <span className="text-xl font-semibold">{`${
                  language?.toLowerCase() === "en" ? "Question" : "Soru"
                } ${i + 1}`}</span>
                <p className="text-gray-600 text-sm">
                  <span>
                    {language?.toLowerCase() === "en"
                      ? "Your Answer:"
                      : "Cevabın:"}
                  </span>{" "}
                  <span className="font-semibold mr-2">
                    {letter}
                    {letter && ")"}
                  </span>
                  {getAnswerText(question, userAnswerId).option}
                </p>
                <p className="text-gray-600 text-sm">
                  <span>
                    {language?.toLowerCase() === "en"
                      ? "Correct Answer:"
                      : "Doğru Cevap:"}
                  </span>{" "}
                  <span className="font-semibold mr-2">
                    {correctLetter}
                    {correctLetter && ")"}
                  </span>
                  {getAnswerText(question, correctAnswerId).option}
                </p>
                <span
                  className={`${
                    userAnswerId !== 0
                      ? isCorrect
                        ? "text-green"
                        : "text-red"
                      : ""
                  } text-lg`}
                  style={{ lineHeight: "1.2rem" }}
                >
                  {userAnswerId === 0
                    ? `〇 ${
                        language?.toLowerCase() === "en"
                          ? "Unanswered"
                          : "Boş Bırakıldı"
                      }`
                    : isCorrect
                    ? `✔ ${language?.toLowerCase() === "en" ? "True" : "Doğru"}`
                    : `✖ ${
                        language?.toLowerCase() === "en" ? "False" : "Yanlış"
                      }`}
                </span>
              </div>
            )
          );
        })}
    </div>
  );
};
